/**
 * The plan editor's tool bar: one button per tool (same shortcuts as the exp
 * editor — V/W/R/D/N/E, plus H for the hand), the view verbs, import and Save.
 *
 * What the armed tool will *create* — thickness, equipment type, parent asset
 * (spec 2026-08-22-02 §7) — lives in the right panel instead, next to the
 * inspector that describes what is already drawn. See `ToolOptionsPanel`.
 */
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import {
    mdiContentSave,
    mdiCursorDefault,
    mdiDoor,
    mdiFileImportOutline,
    mdiAutoFix,
    mdiFitToPageOutline,
    mdiHandBackRightOutline,
    mdiHomeOutline,
    mdiMagnetOn,
    mdiMagnifyMinusOutline,
    mdiMagnifyPlusOutline,
    mdiPipe,
    mdiArrowUpDownBold,
    mdiRedo,
    mdiRulerSquare,
    mdiSelectGroup,
    mdiTargetVariant,
    mdiTextureBox,
    mdiToolboxOutline,
    mdiUndo,
    mdiVectorDifferenceAb,
    mdiVectorLine,
    mdiWindowClosedVariant,
} from '@mdi/js'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import type { ToolId } from './engine/tools'

/** A candidate parent for everything the drawing tools create (§7). */
export interface DrawParent {
    uid: string
    name: string
    typeCode?: string
}

interface EditorToolbarProps {
    tool: ToolId
    onToolChange: (tool: ToolId) => void
    /** Tools that cannot be armed on this frame, with the reason shown as their tooltip. */
    disabledTools?: Partial<Record<ToolId, string>>
    canUndo: boolean
    canRedo: boolean
    onUndo: () => void
    onRedo: () => void
    onFit: () => void
    onZoomIn: () => void
    onZoomOut: () => void
    snap: boolean
    onSnapChange: (on: boolean) => void
    /** The grouping overlay: tints and grouper names (spec 2026-08-30-01 §8). */
    showGroupers: boolean
    onShowGroupersChange: (on: boolean) => void
    dirty: boolean
    saving: boolean
    onSave: () => void
    /**
     * Reason saving is blocked right now, or null. A digitization in flight is
     * one: its create would land under a diff that never saw it (spec
     * 2026-09-03-01 §F2).
     */
    saveBlockedReason?: string | null
    /** Opens the floorplan-v1 import picker (spec 2026-08-22-03 §7). */
    onImport: () => void
    importing: boolean
    /**
     * Opens the digitization dialog (spec 2026-09-03-01 §F1). Hidden entirely
     * when the organization has no vision model; disabled with a reason when
     * there is no scan, the editor is dirty, or a run is in flight.
     */
    onDigitize: () => void
    digitizeAvailable: boolean
    digitizeDisabledReason: string | null
    /**
     * Opens the auto-calibration dialog (spec 2026-09-05-07 §4). Hidden on the
     * same cached 503 as Digitize — one missing model key hides both — and
     * disabled with a reason while the editor is dirty or a run is in flight.
     */
    onAutoCalibrate: () => void
    autoCalibrateDisabledReason: string | null
}

const TOOLS: { id: ToolId; icon: string; shortcut: string }[] = [
    // First, because it is the tool that can do the least: the hand only moves
    // the drawing, and it is where Escape bottoms out.
    { id: 'pan', icon: mdiHandBackRightOutline, shortcut: 'V' },
    { id: 'select', icon: mdiCursorDefault, shortcut: 'S' },
    { id: 'wall', icon: mdiVectorLine, shortcut: 'W' },
    // The split tool: the same polygon gesture as the room, producing a unit
    // (spec 2026-08-30-01 §7). Next to the room on purpose — the difference
    // between the two is what the polygon *is*, not how it is drawn — and
    // **before** it, because that is the order a floor is drawn in and the
    // order the layer panel lists them in (spec 2026-09-06-01 §2).
    //
    // The icons say which is which. Two generic vector-shape glyphs did not:
    // a house outline is a dwelling / lot, a textured box is a room inside
    // one, and neither collides with the line, door, window or toolbox
    // already in the bar.
    { id: 'unit', icon: mdiHomeOutline, shortcut: 'U' },
    { id: 'room', icon: mdiTextureBox, shortcut: 'R' },
    // Right after the room it is cut out of (spec 2026-10-19-07): a pillar, a
    // shaft, a core of toilets. The icon is the boolean difference of two
    // shapes, which is exactly what the tool does.
    { id: 'hole', icon: mdiVectorDifferenceAb, shortcut: 'H' },
    { id: 'door', icon: mdiDoor, shortcut: 'D' },
    { id: 'window', icon: mdiWindowClosedVariant, shortcut: 'N' },
    { id: 'equipment', icon: mdiToolboxOutline, shortcut: 'E' },
    // Network runs — ducts, pipes, cable trays: an open line, after the
    // equipment it feeds. `L` for line; `R` and `N` were taken.
    { id: 'run', icon: mdiPipe, shortcut: 'L' },
    // Risers — where a downpipe, a dry riser or a shaft crosses the floor: a
    // point, after the runs that branch off it. `K` for column (`C` was taken).
    { id: 'riser', icon: mdiArrowUpDownBold, shortcut: 'K' },
    // Calibration is a mode like any other: it owns the pointer while it runs
    // (spec 2026-08-22-03 §1).
    { id: 'calibrate', icon: mdiTargetVariant, shortcut: 'C' },
]

/**
 * The Beta badge sits on the button's corner, out of the row's flow: inline it
 * widened the toolbar enough that "Unsaved changes" wrapped the row onto a
 * second line at 1600 px, so the canvas jumped 40 px on every first edit and
 * back on every save, under the user's pointer.
 */
const BETA_CORNER = 'absolute -right-1.5 -top-2 hidden bg-card px-1 py-0 text-[10px] leading-4 md:inline-flex'

export function EditorToolbar(props: EditorToolbarProps) {
    const { t } = useTranslation('assets')

    return (
        <div
            className="flex flex-wrap items-center gap-2 border-b border-border bg-card px-3 py-2"
            data-testid="plan-editor-toolbar"
        >
            <div className="flex items-center gap-1">
                {TOOLS.map((tool) => {
                    const label = `${t(`planEditor.tools.${tool.id}`)} (${tool.shortcut})`
                    const disabledReason = props.disabledTools?.[tool.id]
                    return (
                        <Button
                            key={tool.id}
                            variant={props.tool === tool.id ? 'default' : 'outline'}
                            size="icon"
                            aria-label={label}
                            aria-pressed={props.tool === tool.id}
                            title={disabledReason ? `${label} · ${disabledReason}` : label}
                            disabled={!!disabledReason}
                            data-testid={`plan-editor-tool-${tool.id}`}
                            onClick={() => props.onToolChange(tool.id)}
                        >
                            <AppIcon path={tool.icon} size="md" />
                        </Button>
                    )
                })}
            </div>

            <span className="mx-1 h-6 w-px bg-border" aria-hidden />

            <div className="flex items-center gap-1">
                <Button
                    variant="outline"
                    size="icon"
                    disabled={!props.canUndo}
                    aria-label={t('planEditor.undo')}
                    title={t('planEditor.undo')}
                    data-testid="plan-editor-undo"
                    onClick={props.onUndo}
                >
                    <AppIcon path={mdiUndo} size="md" />
                </Button>
                <Button
                    variant="outline"
                    size="icon"
                    disabled={!props.canRedo}
                    aria-label={t('planEditor.redo')}
                    title={t('planEditor.redo')}
                    data-testid="plan-editor-redo"
                    onClick={props.onRedo}
                >
                    <AppIcon path={mdiRedo} size="md" />
                </Button>
                <Button
                    variant="outline"
                    size="icon"
                    aria-label={t('planEditor.zoom_out')}
                    title={t('planEditor.zoom_out')}
                    data-testid="plan-editor-zoom-out"
                    onClick={props.onZoomOut}
                >
                    <AppIcon path={mdiMagnifyMinusOutline} size="md" />
                </Button>
                <Button
                    variant="outline"
                    size="icon"
                    aria-label={t('planEditor.zoom_in')}
                    title={t('planEditor.zoom_in')}
                    data-testid="plan-editor-zoom-in"
                    onClick={props.onZoomIn}
                >
                    <AppIcon path={mdiMagnifyPlusOutline} size="md" />
                </Button>
                <Button
                    variant="outline"
                    size="icon"
                    aria-label={`${t('planEditor.fit')} (F)`}
                    title={`${t('planEditor.fit')} (F)`}
                    data-testid="plan-editor-fit"
                    onClick={props.onFit}
                >
                    <AppIcon path={mdiFitToPageOutline} size="md" />
                </Button>
                <Button
                    variant={props.snap ? 'default' : 'outline'}
                    size="icon"
                    aria-label={t('planEditor.snap')}
                    aria-pressed={props.snap}
                    title={t('planEditor.snap_hint')}
                    data-testid="plan-editor-snap"
                    onClick={() => props.onSnapChange(!props.snap)}
                >
                    <AppIcon path={mdiMagnetOn} size="md" />
                </Button>
                <Button
                    variant={props.showGroupers ? 'default' : 'outline'}
                    size="icon"
                    aria-label={t('planEditor.toggle_groupers')}
                    aria-pressed={props.showGroupers}
                    title={t('planEditor.toggle_groupers_hint')}
                    data-testid="plan-editor-toggle-groupers"
                    onClick={() => props.onShowGroupersChange(!props.showGroupers)}
                >
                    <AppIcon path={mdiSelectGroup} size="md" />
                </Button>
            </div>

            <span className="mx-1 h-6 w-px bg-border" aria-hidden />

            {props.digitizeAvailable && (
                <Button
                    variant="outline"
                    size="sm"
                    aria-label={t('planEditor.autoCalibrate.action')}
                    title={props.autoCalibrateDisabledReason ?? t('planEditor.autoCalibrate.hint')}
                    data-testid="plan-editor-autocalibrate"
                    className="relative"
                    disabled={!!props.autoCalibrateDisabledReason}
                    onClick={props.onAutoCalibrate}
                >
                    <AppIcon path={mdiRulerSquare} size="sm" className="md:mr-1" />
                    <span className="hidden md:inline">{t('planEditor.autoCalibrate.action')}</span>
                    <Badge variant="warning" className={BETA_CORNER} title={t('planEditor.beta.tooltip')} data-testid="plan-editor-autocalibrate-beta">
                        {t('planEditor.beta.label')}
                    </Badge>
                </Button>
            )}

            {props.digitizeAvailable && (
                <Button
                    variant="outline"
                    size="sm"
                    aria-label={t('planEditor.digitize.action')}
                    title={props.digitizeDisabledReason ?? t('planEditor.digitize.hint')}
                    data-testid="plan-editor-digitize"
                    className="relative"
                    disabled={!!props.digitizeDisabledReason}
                    onClick={props.onDigitize}
                >
                    <AppIcon path={mdiAutoFix} size="sm" className="md:mr-1" />
                    <span className="hidden md:inline">{t('planEditor.digitize.action')}</span>
                    <Badge variant="warning" className={BETA_CORNER} title={t('planEditor.beta.tooltip')} data-testid="plan-editor-digitize-beta">
                        {t('planEditor.beta.label')}
                    </Badge>
                </Button>
            )}

            <Button
                variant="outline"
                size="sm"
                aria-label={t('planEditor.import')}
                title={t('planEditor.import_hint')}
                data-testid="plan-editor-import"
                disabled={props.importing}
                onClick={props.onImport}
            >
                <AppIcon path={mdiFileImportOutline} size="sm" className="md:mr-1" />
                <span className="hidden md:inline">
                    {props.importing ? t('planEditor.importing') : t('planEditor.import')}
                </span>
            </Button>

            <div className="ml-auto flex items-center gap-2">
                {props.dirty && (
                    <span className="text-xs text-muted-foreground" data-testid="plan-editor-dirty">
                        {t('planEditor.unsaved')}
                    </span>
                )}
                <Button
                    variant="default"
                    size="sm"
                    disabled={!props.dirty || props.saving || !!props.saveBlockedReason}
                    aria-label={t('planEditor.save')}
                    title={props.saveBlockedReason ?? t('planEditor.save')}
                    data-testid="plan-editor-save"
                    onClick={props.onSave}
                >
                    <AppIcon path={mdiContentSave} size="sm" className="md:mr-1" />
                    <span className="hidden md:inline">
                        {props.saving ? t('planEditor.saving') : t('planEditor.save')}
                    </span>
                </Button>
            </div>
        </div>
    )
}
